import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppMenu } from '../components/AppMenu';
import {
  gradeTicket, normPool, groupSummary, formatLocalDateTime, capToRecent7Days,
} from '../lib/downloadGrading';
import { ArrowLeft, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUMMARY_MODES = [
  { value: 'pool', label: 'By Pool' },
  { value: 'track', label: 'By Track' },
  { value: 'trackpool', label: 'By Track / Pool' },
];

export default function MyDownloadsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const localYMD = (offsetDays = 0) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [startDate, setStartDate] = useState(() => localYMD(-1));
  const [endDate, setEndDate] = useState(() => localYMD(0));
  const [summaryMode, setSummaryMode] = useState('pool');
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [payoutsByRace, setPayoutsByRace] = useState({});
  const [grading, setGrading] = useState(false);
  const payoutsCacheRef = useRef({});

  const fetchDownloads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      const r = await fetch(`${API_URL}/api/quickpick/my-downloads?${params.toString()}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setDownloads(data.recent || []);
      }
    } catch (e) {
      console.error('Failed to fetch my downloads:', e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchDownloads(); }, [fetchDownloads]);

  // Grade every download's race against payouts (cached per race).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const gradable = downloads.filter((d) => (d.tickets || []).length && d.race_date);
      const races = {};
      gradable.forEach((d) => { races[`${d.track}|${d.race_number}|${d.race_date}`] = d; });
      const missing = Object.keys(races).filter((k) => !(k in payoutsCacheRef.current));
      if (missing.length) {
        setGrading(true);
        await Promise.all(missing.map(async (k) => {
          const d = races[k];
          try {
            const r = await fetch(
              `${API_URL}/api/quickpick/entries?track=${encodeURIComponent(d.track)}&race=${d.race_number}&date=${d.race_date}`,
              { credentials: 'include' }
            );
            payoutsCacheRef.current[k] = r.ok ? ((await r.json()).payouts || []) : [];
          } catch (e) { payoutsCacheRef.current[k] = []; }
        }));
      }
      if (!cancelled) {
        setPayoutsByRace({ ...payoutsCacheRef.current });
        setGrading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [downloads]);

  const summary = useMemo(
    () => groupSummary(downloads, summaryMode, payoutsByRace),
    [downloads, summaryMode, payoutsByRace]
  );
  const summaryTotals = useMemo(() => {
    return summary.rows.reduce((a, r) => {
      a.total += r.total; a.wagered += r.wagered; a.returned += r.returned;
      a.gradedWagered += r.gradedWagered; return a;
    }, { total: 0, wagered: 0, returned: 0, gradedWagered: 0 });
  }, [summary]);
  const totalRoi = summaryTotals.gradedWagered > 0
    ? ((summaryTotals.returned - summaryTotals.gradedWagered) / summaryTotals.gradedWagered) * 100 : 0;

  // 7-day cap on the DETAIL table only (summary reflects full range).
  const { rows: detailRows, capped, cutoff } = useMemo(
    () => capToRecent7Days(downloads), [downloads]
  );

  // Per-download realized ROI% (graded against race payouts); null when ungraded.
  const rowRoi = useCallback((d) => {
    const tickets = d.tickets || [];
    if (!tickets.length || !d.race_date) return null;
    const payouts = payoutsByRace[`${d.track}|${d.race_number}|${d.race_date}`] || [];
    if (!payouts.length) return null;
    const wag = tickets.reduce((s, t) => s + (t.amount || 0), 0);
    const ret = tickets.reduce((s, t) => s + gradeTicket(t, payouts), 0);
    return wag > 0 ? ((ret - wag) / wag) * 100 : null;
  }, [payoutsByRace]);

  const toggleDetail = useCallback(async (d) => {
    if (expanded === d.id) { setExpanded(null); return; }
    setExpanded(d.id);
    if (details[d.id]) return;
    const tickets = d.tickets || [];
    if (!tickets.length) {
      setDetails((prev) => ({ ...prev, [d.id]: { loading: false, noTickets: true } }));
      return;
    }
    const payouts = payoutsCacheRef.current[`${d.track}|${d.race_number}|${d.race_date}`] || [];
    const graded = payouts.length > 0;
    const wagered = tickets.reduce((s, t) => s + (t.amount || 0), 0);
    const rows = tickets.map((t) => {
      const ret = graded ? gradeTicket(t, payouts) : 0;
      return { pool: t.pool, combo: t.combo, amount: t.amount || 0, ret, win: ret > 0 };
    });
    const returned = rows.reduce((s, x) => s + x.ret, 0);
    const roi = wagered > 0 ? ((returned - wagered) / wagered) * 100 : 0;
    setDetails((prev) => ({ ...prev, [d.id]: { loading: false, graded, wagered, returned, roi, rows } }));
  }, [expanded, details]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-emerald-400 transition"
              data-testid="my-downloads-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-2xl font-bold text-white" data-testid="my-downloads-title">My Downloads</h1>
          </div>
          <AppMenu user={user} onLogout={logout} theme="dark" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <span className="mr-1 text-slate-500">Race date:</span>
            <input
              type="date" data-testid="my-downloads-start-date"
              value={startDate} max={endDate || undefined}
              onChange={(e) => { setStartDate(e.target.value); setExpanded(null); }}
              className="px-2 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
            />
            <span>to</span>
            <input
              type="date" data-testid="my-downloads-end-date"
              value={endDate} min={startDate || undefined}
              onChange={(e) => { setEndDate(e.target.value); setExpanded(null); }}
              className="px-2 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <span className="mr-1 text-slate-500">Summary:</span>
            <select
              data-testid="my-downloads-summary-mode"
              value={summaryMode}
              onChange={(e) => setSummaryMode(e.target.value)}
              className="px-3 py-2 bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg focus:outline-none focus:border-emerald-500"
            >
              {SUMMARY_MODES.map((m) => (<option key={m.value} value={m.value}>{m.label}</option>))}
            </select>
          </div>
          <button
            onClick={fetchDownloads}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-lg transition text-sm"
            data-testid="my-downloads-refresh"
          >
            Refresh
          </button>
          <span className="text-sm text-slate-400 ml-auto">
            Total downloads: <span className="text-emerald-400 font-medium">{downloads.length}</span>
          </span>
        </div>

        {/* Summary */}
        <div className="mb-5 bg-slate-900 border border-slate-800 rounded-xl overflow-hidden" data-testid="my-downloads-summary">
          <div className="px-4 py-2.5 text-sm font-semibold text-slate-200 border-b border-slate-800 flex items-center justify-between">
            <span>{SUMMARY_MODES.find((m) => m.value === summaryMode)?.label} — Wagered · Payouts · ROI</span>
            {grading && <span className="text-xs text-slate-500 font-normal">grading…</span>}
            {!grading && summary.ungraded > 0 && (
              <span className="text-xs text-amber-400 font-normal">{summary.ungraded} not yet graded (payouts unavailable) — excluded from ROI</span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 text-xs uppercase tracking-wide bg-slate-800/40">
                  <th className="text-left px-4 py-2 font-semibold">{summaryMode === 'track' ? 'Track' : summaryMode === 'trackpool' ? 'Track / Pool' : 'Pool'}</th>
                  <th className="text-right px-3 py-2 font-semibold"># Downloads</th>
                  <th className="text-right px-3 py-2 font-semibold">Wagered</th>
                  <th className="text-right px-3 py-2 font-semibold">Payouts</th>
                  <th className="text-right px-4 py-2 font-semibold">ROI</th>
                </tr>
              </thead>
              <tbody>
                {summary.rows.length === 0 && !loading && (
                  <tr><td colSpan="5" className="px-4 py-6 text-center text-slate-500">No downloads in this range.</td></tr>
                )}
                {summary.rows.map((b) => (
                  <tr key={b.key} className="border-t border-slate-800 hover:bg-slate-800/30" data-testid={`my-downloads-summary-row-${b.key}`}>
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium capitalize">{b.label}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-300">{b.total}</td>
                    <td className="px-3 py-2 text-right text-slate-200 font-medium">${b.wagered.toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${b.returned > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>${b.returned.toFixed(2)}</td>
                    <td className={`px-4 py-2 text-right font-bold ${b.gradedWagered <= 0 ? 'text-slate-500' : b.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`my-downloads-summary-roi-${b.key}`}>
                      {b.gradedWagered <= 0 ? 'n/a' : `${b.roi >= 0 ? '+' : ''}${b.roi.toFixed(0)}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
              {summary.rows.length > 1 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-700 bg-slate-800/30 font-semibold">
                    <td className="px-4 py-2 text-slate-200">Total</td>
                    <td className="px-3 py-2 text-right text-slate-300">{summaryTotals.total}</td>
                    <td className="px-3 py-2 text-right text-slate-100">${summaryTotals.wagered.toFixed(2)}</td>
                    <td className={`px-3 py-2 text-right ${summaryTotals.returned > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>${summaryTotals.returned.toFixed(2)}</td>
                    <td className={`px-4 py-2 text-right ${summaryTotals.gradedWagered <= 0 ? 'text-slate-500' : totalRoi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="my-downloads-summary-roi-total">
                      {summaryTotals.gradedWagered <= 0 ? 'n/a' : `${totalRoi >= 0 ? '+' : ''}${totalRoi.toFixed(0)}%`}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* 7-day cap info bar */}
        {capped && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-700 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="my-downloads-cap-info">
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>The summary above reflects your full selected range. The detail list below shows only your most recent 7 days (from {cutoff}) to keep the page fast. Narrow the race-date range to see older detail rows.</span>
          </div>
        )}

        {/* Detail table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700">
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300 w-6"></th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Downloaded</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Track</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Race</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Race Date</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Pool</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Format</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Tickets</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-300">Wager</th>
                  <th className="text-right px-4 py-3 text-sm font-semibold text-slate-300">ROI</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-400">Loading your downloads...</td></tr>
                ) : detailRows.length === 0 ? (
                  <tr><td colSpan="10" className="px-4 py-8 text-center text-slate-400">No downloads recorded yet.</td></tr>
                ) : detailRows.map((d) => {
                  const open = expanded === d.id;
                  const det = details[d.id];
                  const roi = rowRoi(d);
                  return (
                    <React.Fragment key={d.id}>
                      <tr
                        className="border-b border-slate-800 hover:bg-slate-800/30 cursor-pointer"
                        onClick={() => toggleDetail(d)}
                        data-testid={`my-download-row-${d.id}`}
                      >
                        <td className="px-4 py-3 text-slate-500 text-sm">{open ? '▾' : '▸'}</td>
                        <td className="px-4 py-3 text-slate-400 text-sm">{formatLocalDateTime(d.downloaded_at)}</td>
                        <td className="px-4 py-3 text-white font-medium text-sm">{d.track}</td>
                        <td className="px-4 py-3 text-slate-300 text-sm">R{d.race_number}</td>
                        <td className="px-4 py-3 text-sm font-medium text-amber-300">{d.race_date || <span className="text-slate-500">—</span>}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium">{d.pool}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-300 text-sm capitalize">{d.format}</td>
                        <td className="px-4 py-3 text-slate-300 text-sm">{d.ticket_count}</td>
                        <td className="px-4 py-3 text-emerald-400 font-medium text-sm">${d.total_wager?.toFixed(2)}</td>
                        <td className={`px-4 py-3 text-right font-bold text-sm ${roi === null ? 'text-slate-500' : roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid={`my-download-roi-${d.id}`}>
                          {roi === null ? 'n/a' : `${roi >= 0 ? '+' : ''}${roi.toFixed(0)}%`}
                        </td>
                      </tr>
                      {open && (
                        <tr className="bg-slate-950/60 border-b border-slate-800" data-testid={`my-download-detail-${d.id}`}>
                          <td colSpan="10" className="px-6 py-4">
                            {det?.noTickets ? (
                              <div className="text-slate-500 text-sm italic">Ticket details were not recorded for this download (older entry).</div>
                            ) : det ? (
                              <div>
                                <div className="flex flex-wrap gap-2 items-center mb-3 text-xs">
                                  <span className="rounded bg-slate-800 border border-slate-700 px-2 py-1"><span className="text-slate-400">Wagered </span><span className="font-bold text-white">${det.wagered.toFixed(2)}</span></span>
                                  {det.graded ? (
                                    <>
                                      <span className="rounded bg-slate-800 border border-slate-700 px-2 py-1"><span className="text-slate-400">Returned </span><span className={`font-bold ${det.returned > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>${det.returned.toFixed(2)}</span></span>
                                      <span className={`rounded px-2 py-1 border ${det.roi >= 0 ? 'bg-emerald-500/10 border-emerald-700' : 'bg-red-500/10 border-red-800'}`}><span className="text-slate-400">ROI </span><span className={`font-bold ${det.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{det.roi >= 0 ? '+' : ''}{det.roi.toFixed(0)}%</span></span>
                                    </>
                                  ) : (
                                    <span className="rounded bg-amber-500/10 border border-amber-700 text-amber-400 px-2 py-1">Not yet graded — race payouts unavailable</span>
                                  )}
                                </div>
                                {d.inputs && (() => {
                                  const inp = d.inputs;
                                  const pns = [...new Set([
                                    ...Object.keys(inp.myOdds || {}), ...Object.keys(inp.dontUse || {}),
                                    ...Object.keys(inp.keySel || {}), ...Object.keys(inp.grid || {}),
                                    ...Object.keys(inp.bestFin || {}), ...Object.keys(inp.worstFin || {}),
                                    ...Object.keys(inp.straightBet || {}),
                                  ])].sort((a, b) => (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0));
                                  const POSL = { 0: 'Any', 1: '1st', 2: '2nd', 3: '3rd', 4: '4th' };
                                  return (
                                    <div className="mb-3 rounded-lg border border-slate-800 bg-slate-900/60 p-3" data-testid={`my-download-inputs-${d.id}`}>
                                      <div className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-2">Inputs used</div>
                                      <div className="flex flex-wrap gap-2 text-[11px] mb-2">
                                        <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Mode: <b className="text-white capitalize">{inp.mode || '—'}</b></span>
                                        <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Coverage: <b className="text-white">{inp.coverage === 'exclude' ? 'Exclude Low-Prob' : 'Full'}</b></span>
                                        <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Odds: <b className="text-white capitalize">{inp.oddsSource || '—'}</b>{inp.blendPct ? ` (blend ${inp.blendPct}%)` : ''}</span>
                                        {Array.isArray(inp.pools) && inp.pools.length > 0 && <span className="rounded bg-slate-800 px-2 py-0.5 text-slate-300">Pools: <b className="text-white">{inp.pools.join(', ')}</b></span>}
                                      </div>
                                      {pns.length > 0 ? (
                                        <div className="overflow-x-auto">
                                          <table className="text-[11px] w-auto">
                                            <thead><tr className="text-slate-500">
                                              <th className="text-left pr-4">#</th><th className="text-right pr-4">MyOdds</th>
                                              <th className="text-center pr-4">Key</th>
                                              <th className="text-center pr-4">Best</th><th className="text-center pr-4">Worst</th>
                                              <th className="text-center pr-4">Positions</th>
                                              <th className="text-center pr-4">Straight</th><th className="text-center">Dont-Use</th>
                                            </tr></thead>
                                            <tbody>
                                              {pns.map((pn) => (
                                                <tr key={pn} className="text-slate-300" data-testid={`my-download-input-row-${d.id}-${pn}`}>
                                                  <td className="pr-4 font-bold text-white">{pn}</td>
                                                  <td className="text-right pr-4">{inp.myOdds?.[pn] ?? '—'}</td>
                                                  <td className="text-center pr-4 text-amber-400">{inp.keySel?.[pn] ? '★' : ''}</td>
                                                  <td className="text-center pr-4">{POSL[inp.bestFin?.[pn] || 0]}</td>
                                                  <td className="text-center pr-4">{inp.worstFin?.[pn] ? POSL[inp.worstFin[pn]] : 'Any'}</td>
                                                  <td className="text-center pr-4">{Array.isArray(inp.grid?.[pn]) && inp.grid[pn].length ? inp.grid[pn].slice().sort((a, b) => a - b).map((p) => POSL[p]).join('/') : '—'}</td>
                                                  <td className="text-center pr-4">{inp.straightBet?.[pn] || '—'}</td>
                                                  <td className="text-center text-red-400">{inp.dontUse?.[pn] ? '✓' : ''}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      ) : <div className="text-slate-500 text-[11px] italic">No per-runner overrides (defaults used).</div>}
                                    </div>
                                  );
                                })()}
                                <div className="flex flex-wrap gap-1.5 font-mono text-xs">
                                  {det.rows.map((r, i) => {
                                    const straight = /^(WIN|PLC|PLACE)/i.test(r.combo || '');
                                    return (
                                      <span key={i} className={`px-2 py-1 rounded border ${r.win ? 'bg-emerald-500/15 border-emerald-700' : 'bg-slate-800 border-slate-700'}`}>
                                        {!straight && <><span className="text-slate-500">{r.pool}</span>{' '}</>}
                                        <span className={r.win ? 'text-emerald-300 font-bold' : 'text-slate-200'}>{r.combo}</span>{' '}
                                        <span className="text-amber-400">${r.amount.toFixed(2)}</span>
                                        {det.graded && r.win && <span className="text-emerald-400 font-semibold"> ✓${r.ret.toFixed(2)}</span>}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
